import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import useLifecyclePersonsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Dialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`;

// #72 — yerel Btn kaldırıldı, paylaşılan Button'a bağlandı ($primary →
// variant="outline" YOKLUĞU/VARLIĞI ile eşleniyor, bkz. t6-report.md).

const STATUS_OPTIONS = ['candidate', 'hire', 'employee', 'leaver', 'alumni'];

export default function LifecyclePersonsPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [statusDialog, setStatusDialog] = useState({ isOpen: false, id: null, currentStatus: '', newStatus: '' });
  const [updating, setUpdating] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, setStatus, statusLabel } = useLifecyclePersonsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleSetStatusClick = useCallback((row) => {
    setStatusDialog({ isOpen: true, id: row.id, currentStatus: row.status, newStatus: row.status });
  }, []);

  const handleSetStatusConfirm = useCallback(async () => {
    if (!statusDialog.id || !statusDialog.newStatus || statusDialog.newStatus === statusDialog.currentStatus) {
      setStatusDialog({ isOpen: false, id: null, currentStatus: '', newStatus: '' });
      return;
    }
    setUpdating(true);
    await setStatus(statusDialog.id, statusDialog.newStatus);
    setStatusDialog({ isOpen: false, id: null, currentStatus: '', newStatus: '' });
    setUpdating(false);
  }, [statusDialog, setStatus]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    return [
      { type: 'edit', label: t('lifecyclePerson.setStatus'), onClick: handleSetStatusClick },
    ];
  }, [t, handleSetStatusClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={buildActions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <Dialog $open={statusDialog.isOpen}>
        <Card>
          <h3 style={{ margin: '0 0 16px' }}>{t('lifecyclePerson.setStatusTitle')}</h3>
          <p style={{ marginBottom: 16, color: '#64748b' }}>
            {t('lifecyclePerson.setStatusDescription')}
          </p>
          <FormControl variant="standard" fullWidth>
            <InputLabel>{t('lifecyclePerson.status.label')}</InputLabel>
            <Select
              value={statusDialog.newStatus}
              onChange={(e) => setStatusDialog((s) => ({ ...s, newStatus: e.target.value }))}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <ButtonRow>
            <Button
              variant="outline"
              onClick={() => setStatusDialog({ isOpen: false, id: null, currentStatus: '', newStatus: '' })}
              disabled={updating}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSetStatusConfirm}
              disabled={updating || !statusDialog.newStatus || statusDialog.newStatus === statusDialog.currentStatus}
            >
              {t('common.save')}
            </Button>
          </ButtonRow>
        </Card>
      </Dialog>

      {useMemo(() => (
        <DataTable
          title={t('lifecyclePerson.title')}
          fetching={fetching || updating}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, updating, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
